<script setup lang="ts">
import { type ButtonProps } from '#ui/types';

type FiltersAdvancedModalButtonVariants = 'collapsed' | 'expanded';

// PROPS
interface Props {
    variant?: FiltersAdvancedModalButtonVariants;
}

const { variant = 'collapsed' } = defineProps<Props>();

// EMITS
defineEmits<{
    openContactForm: [];
}>();

// COMPOSABLES
const { tt } = useTypedI18n();
const sellingMethod = useSlpSellingMethod();
const { reset } = useSlpParams(sellingMethod);
const { hasAppliedFilters, totalItems, appliedFiltersCounter, isPending } = useSlpData();

// DATA
const chipText = computed(() => (appliedFiltersCounter.value > 9 ? '9+' : appliedFiltersCounter.value));
const ui = {
    footer: 'w-full sm:flex-row sm:justify-between',
};
const buttonProps = computed<Partial<ButtonProps>>(() => {
    switch (variant) {
        case 'collapsed':
            return {
                variant: 'link',
            };
        case 'expanded':
            return {
                variant: 'outline',
                block: true,
                label: tt('slp.filters.advancedModal.title'),
            };
    }
});
</script>

<template>
    <UModal fullscreen :title="tt('slp.filters.advancedModal.title')" v-bind="modalNoDescAriaFixupProps" :ui="ui">
        <UChip as="span" :text="chipText" size="4xl" inset :show="appliedFiltersCounter > 0">
            <UButton color="neutral" leading-icon="lucide:sliders-horizontal" size="xl" v-bind="buttonProps" />
        </UChip>
        <template #description>
            <BaseI18nT :keypath="`slp.page.results`" tag="p" class="text-dimmed font-medium">
                <template #total>
                    <UIcon v-if="isPending" name="i-svg-spinners-270-ring" class="h-4 w-4" />
                    <strong v-else>{{ totalItems.current }}</strong>
                </template>
            </BaseI18nT>
        </template>
        <template #body>
            <div class="flex flex-col gap-5">
                <SlpFiltersAdvanced />
            </div>
        </template>
        <template #footer="{ close }">
            <div>
                <UButton
                    v-if="hasAppliedFilters"
                    color="primary"
                    variant="ghost"
                    :label="tt('slp.filters.advancedModal.cancelAll')"
                    size="xl"
                    @click="reset"
                />
            </div>
            <UButton
                v-if="!isPending && totalItems.current === 0"
                :label="tt('slp.filters.advancedModal.contact')"
                color="primary"
                variant="solid"
                size="xl"
                @click="
                    () => {
                        close();
                        $emit('openContactForm');
                    }
                "
            />
            <UButton v-else color="primary" variant="solid" size="xl" @click="close">
                <BaseI18nT keypath="slp.filters.advancedModal.show" tag="span" :plural="totalItems.current">
                    <template #count>
                        <UIcon v-if="isPending" name="i-svg-spinners-270-ring" class="h-4 w-4" />
                        <strong v-else>{{ totalItems.current }}</strong>
                    </template>
                </BaseI18nT>
            </UButton>
        </template>
    </UModal>
</template>
